import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../auth/auth.service';
import { AddCandidateService } from '../services/add-candidate.service';

interface CandidatePayload {
  JAId: string;
  MCId: string;
  email: string;
  phone: string;
  jobTitle: string;
  timestamp: number;
  expiry: number;
}

@Component({
  selector: 'app-add-candidate',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './add-candidate.component.html',
  styleUrls: ['./add-candidate.component.css']
})
export class AddCandidateComponent implements OnInit {
  isLoading = false;
  errorMessage = '';
  candidateData: CandidatePayload | null = null;
  showConfirmation = false;
  isProcessing = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private addCandidateService: AddCandidateService
  ) {}

  ngOnInit(): void {
    // Check authentication first
    if (!this.authService.isLoggedIn) {
      // User needs to login first, redirect to dashboard and let session monitor handle it
      this.router.navigate(['/dashboard']);
      return;
    }

    // Get encrypted payload from URL params
    this.route.queryParams.subscribe(params => {
      const encryptedData = params['data'];
      if (encryptedData) {
        this.processEncryptedPayload(encryptedData);
      } else {
        this.errorMessage = 'No candidate data found in URL';
      }
    });
  }

  private async processEncryptedPayload(encryptedData: string): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Call backend to decrypt and validate the payload
      const response = await this.addCandidateService.decryptCandidatePayload(encryptedData);
      
      if (response.success && response.data) {
        this.candidateData = response.data.decryptedPayload;
        
        // Check if MCId matches current user
        if (response.data.verified) {
          // Show success toast for MC verification
          this.showToast('Verified MC user', 'success');
          // Show confirmation dialog
          this.showConfirmation = true;
        } else {
          this.errorMessage = 'MCId verification failed. You are not authorized to add this candidate.';
        }
      } else {
        this.errorMessage = response.error || 'Failed to process candidate data';
      }
    } catch (error) {
      console.error('Error processing candidate payload:', error);
      this.errorMessage = 'Failed to process candidate data. Please check if the link is valid and not expired.';
    } finally {
      this.isLoading = false;
    }
  }

  async onConfirmAddCandidate(): Promise<void> {
    if (!this.candidateData) return;

    this.isProcessing = true;
    this.errorMessage = '';

    try {
      const response = await this.addCandidateService.createContactFromCandidate(this.candidateData);
      
      if (response.success) {
        this.showToast('Contact created successfully', 'success');
        
        // Redirect to dashboard contacts tab after a short delay
        setTimeout(() => {
          this.router.navigate(['/dashboard'], { 
            queryParams: { tab: 'contacts' } 
          });
        }, 2000);
      } else {
        this.errorMessage = response.error || 'Failed to create contact';
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      this.errorMessage = 'Failed to create contact. Please try again.';
    } finally {
      this.isProcessing = false;
    }
  }

  onCancelAddCandidate(): void {
    // Redirect back to dashboard
    this.router.navigate(['/dashboard']);
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    // For now, we'll use console.log. Later we can integrate with a proper toast service
    console.log(`Toast (${type}): ${message}`);
    
    // Create a simple toast notification
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 24px;
      border-radius: 4px;
      color: white;
      font-weight: 500;
      z-index: 10000;
      animation: slideIn 0.3s ease-in-out;
      ${type === 'success' ? 'background-color: #10b981;' : 'background-color: #ef4444;'}
    `;

    document.body.appendChild(toast);

    // Remove toast after 3 seconds
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease-in-out';
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 3000);
  }
}